import i18nFactory from '@yandex-int/i18n';
import { forEach, get } from 'lodash';
import createReReSelector from 're-reselect';
import { createSelector } from 'reselect';

import { ShowConditionsRecommendationData } from '../../../components/EditGroupRecommendation/EditGroupRecommendation';
import { TitleItem } from '../../../components/InlineTitleSelector/InlineTitleSelector';
import { defineSmartFilterBudgetCalculation } from '../../../mappers/group/group';
import { mapCheckedPageBlocksData } from '../../../mappers/placementsMap/placementsMap';
import { getMultiplierFromRateCorrectionValue } from '../../../mappers/rateCorrections/rateCorrections';
import { audienceTargetingsTypeOrderedList } from '../../../service/CryptaHelpers/CryptaHelpers';
import { EditGroupValidationParams } from '../../../service/GroupEditErrors/ClientGroupEditErrors/ClientGroupEditErrors';
import { GroupsHelpers } from '../../../service/GroupsHelpers/GroupsHelpers';
import { getGroupsEditMassActions } from '../../../service/GroupsMassActionsHelpers/GroupsMassActionsHelpers';
import { LangUtils } from '../../../service/Lang/Lang';
import { PlacementsMapHelpers } from '../../../service/PlacementsMap/PlacementsMap';
import { PredictorHelpers } from '../../../service/PredictorHelpers/PredictorHelpers';
import {
    getRecommendationIdContext,
    RecommendationСharacterWeight
} from '../../../service/RecommendationsHelpers/RecommendationsHelpers';
import { RegionsHelpers } from '../../../service/RegionsHelpers/RegionsHelpers';
import { AudienceTargetingsType } from '../../../typings/audienceTargetings';
import { AccessRights, groups, Region, ValidationErrorData, ValidationErrorsData } from '../../../typings/common';
import { ConstData } from '../../../typings/constants';
import { SegmentCategoryText } from '../../../typings/crypta-segment-selector';
import {
    DisplayListItem,
    DisplayPageBlock,
    IndoorMapCommonData,
    IndoorMapEditorState,
    MapType,
    OutdoorMapCommonData,
    OutdoorMapEditorState,
    SelectedScreens
} from '../../../typings/displayMap';
import { CampaignType, GroupType } from '../../../typings/domain';
import {
    CurrencyCode,
    GdBidModifierDemographicInput,
    GdBidModifierType,
    GdGeotreeType,
    GdMobileOsTypeInput,
    OperatorFeaturesFragment
} from '../../../typings/graphql-grid';
import { GroupId, SmartGroupBudgetCalculation, TypeModeGroupItems } from '../../../typings/groups';
import {
    CpmGroupShowConditionType,
    EditCpmGroupDataValue,
    EditCpmPriceGroupDataValue,
    EditGroupData,
    EditGroupDataValue,
    EditGroupRegions,
    EditGroupsState,
    ProductsListItemRestriction
} from '../../../typings/groupsEditState';
import { PlatformType, PredictorData, PredictorGeo, TextPredictor } from '../../../typings/predictor';
import {
    DemographyRateCorrectionCondition,
    DeviceRateCorrectionCondition,
    DeviceRateCorrectionType,
    RateCorrectionType,
    RetargetingConditionDataForRateCorrections
} from '../../../typings/ratesCorrections';
import {
    EditPageRecommendationsData,
    Recommendation,
    RecommendationModalType,
    RecommendationsAgregator,
    RecommendationsModalState
} from '../../../typings/recommendations';
import { RegionsEditorState } from '../../../typings/regionsEditor';
import { RetargetingConditionData } from '../../../typings/retargeting';
import { ClientFeatures, Regions, State } from '../../../typings/state';
import {
    getAudienceTargetingsText,
    getCurrentAudienceTargetingsText
} from '../../audienceTargetings/audienceTargetings';
import {
    filterGroupIndoorMapData,
    filterGroupOutdoorMapData,
    selectGroupIndoorValue,
    selectGroupOutdoorValue
} from '../../placementsMap/placementsMap';
import { getRecommendationAgregatorData } from '../../recommendations';

import * as keyset from './group.i18n';

const i18n = i18nFactory(keyset);

export const selectRetargetingConditionsDataForGroupsEdit = createSelector(
    (state: State) => state.editGroups.retargetingConditions,
    (
        retargetingConditionsData?: Array<RetargetingConditionData>
    ): Array<RetargetingConditionDataForRateCorrections> => {
        return retargetingConditionsData ? retargetingConditionsData.map(({ id, name }) => ({ id, name })) : [];
    }
);

export const mapData = {
    [MapType.indoor]: `${[MapType.indoor]}MapData`,
    [MapType.outdoor]: `${[MapType.outdoor]}MapData`
};

export const mapEditor = {
    [MapType.indoor]: `${[MapType.indoor]}MapEditor`,
    [MapType.outdoor]: `${[MapType.outdoor]}MapEditor`
};

export const selectEditGroupInfo = (state: State, groupId: GroupId): EditGroupData => {
    return state.editGroups.groups[groupId];
};

export const selectEditGroupValueInfo = (state: State, groupId: GroupId): EditGroupDataValue => {
    return state.editGroups.groups[groupId].value;
};

export const selectEditGroup = (state: EditGroupsState, groupId: GroupId): EditGroupData => state.groups[groupId];

export const selectLibraryListItems = createReReSelector(
    (state: State, groupId: string) => state.editGroups.groups[groupId],
    (group: EditGroupData) => {
        switch (group.value.campaignType) {
            case CampaignType.TEXT:
            case CampaignType.PERFORMANCE: {
                const { selectedLibraryList = [] } = group.value;

                return selectedLibraryList.map(item => item.name);
            }
            default:
                return [];
        }
    }
)((state, groupId) => groupId);

export const selectEditGroups = (state: State, groupId: GroupId): EditGroupData => {
    return state.editGroups.groups[groupId];
};

export const selectRegionTree = createSelector(
    (state: State) => state.commonData.regions,
    (state: State) => state.commonData.client.translocalGeotreeType,
    (regions?: Regions, translocalGeotreeType?: GdGeotreeType): Region[] => {
        return regions ? RegionsHelpers.getRegionsTree(regions, LangUtils.getCurrentLang(), translocalGeotreeType) : [];
    }
);

export const selectRegionTreeByType = createSelector(
    (state: State, geoTreeType: GdGeotreeType | undefined) => geoTreeType,
    (state: State) => state.commonData.regions,
    (geoTreeType?: GdGeotreeType, regions?: Regions): Region[] => {
        return regions ? RegionsHelpers.getRegionsTree(regions, LangUtils.getCurrentLang(), geoTreeType) : [];
    }
);

export const selectRegionsEditGroup = (state: State, groupId: GroupId): EditGroupRegions => {
    const group = selectEditGroupInfo(state, groupId);

    switch (group.value.campaignType) {
        case CampaignType.TEXT:
        case CampaignType.MCBANNER:
        case CampaignType.PERFORMANCE:
        case CampaignType.INTERNAL_DISTRIB:
        case CampaignType.INTERNAL_FREE:
        case CampaignType.INTERNAL_AUTOBUDGET:
        case CampaignType.CPM_BANNER:
        case CampaignType.CONTENT_PROMOTION:
        case CampaignType.CPM_PRICE:
            return group.value.regions;
        default:
            throw new Error('Unsupported campaignType');
    }
};

export const selectErrorsEditGroup = createSelector(
    (state: State, groupId: GroupId, fieldName: string) => {
        const group = selectEditGroupInfo(state, groupId);

        if (group === undefined) {
            return undefined;
        }

        return group.errors[fieldName];
    },
    (errorsCurrentField: { errors?: ValidationErrorData[] } | undefined): ValidationErrorData[] => {
        if (errorsCurrentField === undefined) {
            return [];
        }

        return errorsCurrentField.errors || [];
    }
);

export const selectGroupValidationParams = createSelector(
    (state: State) => state.commonData.client.workCurrency,
    (state: State) => state.commonData.constants,
    (workCurrency: CurrencyCode, constants?: ConstData): EditGroupValidationParams => {
        if (!constants) {
            throw Error('No constants data');
        }

        const currentCurrencyConstants = constants.currencyConstants[workCurrency];

        return {
            minGeneralPrice: currentCurrencyConstants.minPrice,
            maxGeneralPrice: currentCurrencyConstants.maxPrice,
            currency: workCurrency
        };
    }
);

export const selectCpmGroupValidationParams = createSelector(
    (state: State) => state.commonData.client.workCurrency,
    (state: State) => state.commonData.constants,
    (workCurrency: CurrencyCode, constants?: ConstData): EditGroupValidationParams => {
        if (!constants) {
            throw Error('No constants data');
        }

        const currentCurrencyConstants = constants.currencyConstants[workCurrency];

        return {
            minGeneralPrice: currentCurrencyConstants.minCpmPrice,
            maxGeneralPrice: currentCurrencyConstants.maxCpmPrice,
            currency: workCurrency
        };
    }
);

export const selectOperatorFeatures = (state: State): OperatorFeaturesFragment => state.commonData.operatorFeatures;

export const selectClientFeatures = (state: State): ClientFeatures => state.commonData.features;

export const selectAccessRights = (state: State): AccessRights => state.commonData.accessRights;

interface SelectEditableGroupTypesInput {
    operatorFeatures: OperatorFeaturesFragment;
    accessRights: AccessRights;
    clientFeatures?: ClientFeatures;
}

export const selectEditableGroupTypes = (params: SelectEditableGroupTypesInput): Set<GroupType> => {
    const { operatorFeatures, accessRights, clientFeatures } = params;
    const editableGroupTypes: Set<GroupType> = new Set();

    if (operatorFeatures.isAdGroupUpdateAllowed) {
        editableGroupTypes.add(GroupType.TEXT);
    }

    if (operatorFeatures.isPerformanceAdEditAllowed) {
        editableGroupTypes.add(GroupType.PERFORMANCE);
    }

    if (operatorFeatures.isCpmAdGroupUpdateAllowed) {
        editableGroupTypes.add(GroupType.CPM_BANNER);
    }

    if (operatorFeatures.isOutdoorGroupsEditAllowed) {
        editableGroupTypes.add(GroupType.CPM_OUTDOOR);
    }

    if (operatorFeatures.isIndoorGroupsEditAllowed) {
        editableGroupTypes.add(GroupType.CPM_INDOOR);
    }

    if (operatorFeatures.isVideoGroupsEditAllowed) {
        editableGroupTypes.add(GroupType.CPM_VIDEO);
    }

    if (operatorFeatures.isAudioGroupsEditAllowed) {
        editableGroupTypes.add(GroupType.CPM_AUDIO);
    }

    if (operatorFeatures.isGeoproductGroupsEditAllowed) {
        editableGroupTypes.add(GroupType.CPM_GEOPRODUCT);
    }

    if (accessRights.canEditInternalAds) {
        editableGroupTypes.add(GroupType.INTERNAL);
    }

    if (operatorFeatures.isMcBannerDnaEnabled) {
        editableGroupTypes.add(GroupType.MCBANNER);
    }

    if (
        operatorFeatures.isAdGroupUpdateAllowed &&
        clientFeatures &&
        clientFeatures.isContentPromotionVideoInGridSupported
    ) {
        editableGroupTypes.add(GroupType.CONTENT_PROMOTION_VIDEO);
    }

    if (
        operatorFeatures.isAdGroupUpdateAllowed &&
        clientFeatures &&
        clientFeatures.isContentPromotionCollectionInGridSupported
    ) {
        editableGroupTypes.add(GroupType.CONTENT_PROMOTION_COLLECTION);
    }

    editableGroupTypes.add(GroupType.CPM_PRICE); // todo @sandramav нужна фича от бека

    return editableGroupTypes;
};

export const selectAddableGroupTypes = (
    features: OperatorFeaturesFragment,
    accessRights: AccessRights
): Array<CampaignType> => {
    const addableGroupTypes: Array<CampaignType> = [];

    if (features.isAdGroupUpdateAllowed) {
        addableGroupTypes.push(CampaignType.TEXT);
        addableGroupTypes.push(CampaignType.CONTENT_PROMOTION);
    }

    if (features.isPerformanceAdEditAllowed) {
        addableGroupTypes.push(CampaignType.PERFORMANCE);
    }

    if (accessRights.canEditInternalAds) {
        addableGroupTypes.push(CampaignType.INTERNAL_DISTRIB);
        addableGroupTypes.push(CampaignType.INTERNAL_FREE);
    }

    if (features.isCpmAdGroupUpdateAllowed) {
        addableGroupTypes.push(CampaignType.CPM_BANNER);
    }

    if (features.isMcBannerDnaEnabled) {
        addableGroupTypes.push(CampaignType.MCBANNER);
    }

    addableGroupTypes.push(CampaignType.CPM_PRICE);

    return addableGroupTypes;
};

export const selectCampaignTypesForBannerCreation = (
    features: OperatorFeaturesFragment,
    accessRights: AccessRights
): Array<CampaignType> => {
    const addableCampaignTypes: Array<CampaignType> = [];

    if (features.isCpmBannerUpdateAllowed) {
        addableCampaignTypes.push(CampaignType.CPM_BANNER);
    }

    if (features.isMcBannerDnaEnabled) {
        addableCampaignTypes.push(CampaignType.MCBANNER);
    }

    if (features.isBannerUpdateAllowed) {
        addableCampaignTypes.push(CampaignType.TEXT);
    }

    if (features.isPerformanceAdEditAllowed) {
        addableCampaignTypes.push(CampaignType.PERFORMANCE);
    }

    if (accessRights.canEditInternalAds) {
        addableCampaignTypes.push(CampaignType.INTERNAL_DISTRIB);
        addableCampaignTypes.push(CampaignType.INTERNAL_FREE);
    }

    addableCampaignTypes.push(CampaignType.CONTENT_PROMOTION);

    return addableCampaignTypes;
};

export const selectCampaignsCount = createSelector(
    (state: State) => state.editGroups.groups,
    (editGroups: Record<GroupId, EditGroupData>): number => {
        const campaignArr: Array<string> = [];

        forEach(editGroups, group => {
            if (!campaignArr.includes(group.campaign.id)) {
                campaignArr.push(group.campaign.id);
            }
        });

        return campaignArr.length;
    }
);

export const getSmartGroupBudgetCalculation = (state: State, groupId?: GroupId): SmartGroupBudgetCalculation => {
    let strategyType;
    if (groupId) {
        const group = state.editGroups.groups[groupId];
        strategyType = group.campaign.strategyType;
    }

    return defineSmartFilterBudgetCalculation(strategyType);
};

export const selectAvailableGroupsIdsForMassActions = createSelector(
    (state: State) => state.editGroups.groups,
    (editGroups: Record<GroupId, EditGroupData>) => {
        return Object.keys(editGroups).filter(key => {
            return (
                editGroups[key].value.campaignType === CampaignType.TEXT ||
                editGroups[key].value.campaignType === CampaignType.MCBANNER
            );
        });
    }
);

export const selectGroupsMassActionsItems = createSelector(
    selectAvailableGroupsIdsForMassActions,
    (state: State) => state.commonData.operatorFeatures.isAdsMassEditAllowed,
    (groupsIds: string[], isAdsMassEditAllowed: boolean) => {
        if (isAdsMassEditAllowed && groupsIds.length > 1) {
            return getGroupsEditMassActions();
        }

        return [];
    }
);

export const getGroupShowConditionsRecommendationText = (type: CampaignType | undefined): string => {
    switch (type) {
        case CampaignType.TEXT:
            return i18n('tgo-recommendation-text');
        case CampaignType.PERFORMANCE:
            return i18n('performance-recommendation-text');
        case CampaignType.MCBANNER:
            return i18n('mc-banner-recommendation-text');
        default:
            return '';
    }
};

const getGroupShowConditionsRecommendationData = (
    group: EditGroupDataValue,
    wasClosed: boolean
): ShowConditionsRecommendationData => {
    const recommendation: ShowConditionsRecommendationData = {
        isVisible: false,
        text: getGroupShowConditionsRecommendationText(group.campaignType),
        wasClosed
    };

    switch (group.campaignType) {
        case CampaignType.TEXT: {
            if (!group.autotargeting.isActive && group.keywords.length === 0 && group.retargeting.length === 0) {
                recommendation.isVisible = true;
            }

            return recommendation;
        }

        case CampaignType.MCBANNER: {
            if (group.keywords.length === 0) {
                recommendation.isVisible = true;
            }

            return recommendation;
        }

        case CampaignType.PERFORMANCE: {
            if (group.filters.length === 0) {
                recommendation.isVisible = true;
            }

            return recommendation;
        }

        default:
            return recommendation;
    }
};

export const selectShowConditionsRecommendationData = createSelector(
    (state: State) => state.editGroups.groups,
    (editGroups: Record<GroupId, EditGroupData>) => {
        return Object.keys(editGroups).reduce(
            (acc: Record<GroupId, ShowConditionsRecommendationData>, groupId: GroupId) => {
                return {
                    ...acc,
                    [groupId]: getGroupShowConditionsRecommendationData(
                        editGroups[groupId].value,
                        editGroups[groupId].showConditionsRecommendationData.wasClosed
                    )
                };
            },
            {}
        );
    }
);

export const selectFieldErrorsByFilterErrors = createSelector(
    (state: State) => state.editGroups.groupFilterInspector.filter.errors,
    (errors?: ValidationErrorsData): Record<string, Array<ValidationErrorData>> => {
        const fields = [
            'priceCpc',
            'priceCpa',
            'name',
            'priceRange',
            'vendors',
            'categoryIds',
            'allConditions',
            'filterItself'
        ];

        return fields.reduce((errorsObj, field) => {
            errorsObj[field] = errors && errors[field] && errors[field].errors ? errors[field].errors : [];
            return errorsObj;
        }, {});
    }
);

export const selectRestrictionsForCpmProduct = createSelector(
    (state: State) => state.editGroups,
    (editGroupsState: EditGroupsState): ProductsListItemRestriction[] => {
        const { productsList } = editGroupsState;

        if (productsList === undefined || productsList[CampaignType.CPM_BANNER] === undefined) {
            throw Error('Для продукта CPM_BANNER нет данных');
        }

        return productsList[CampaignType.CPM_BANNER].restrictions;
    }
);

export const selectRestrictionsForCpmPriceProduct = createSelector(
    (state: State) => state.editGroups,
    (editGroupsState: EditGroupsState): ProductsListItemRestriction[] => {
        const { productsList } = editGroupsState;

        if (productsList === undefined || productsList[CampaignType.CPM_PRICE] === undefined) {
            throw Error('Для продукта CPM_PRICE нет данных');
        }

        return productsList[CampaignType.CPM_PRICE].restrictions;
    }
);

export const selectRestrictionForGroup = createSelector(
    selectRestrictionsForCpmProduct,
    selectEditGroupValueInfo,
    (
        restrictions: ProductsListItemRestriction[],
        groupInfoValue: EditCpmGroupDataValue
    ): ProductsListItemRestriction => {
        const { productRestrictionId } = groupInfoValue;
        const currentRestriction = restrictions.find(restriction => restriction.id === productRestrictionId);

        if (currentRestriction === undefined) {
            throw Error('задан неверный id продукта');
        }

        return currentRestriction;
    }
);

export const selectRestrictionForCPMPriceGroup = createSelector(
    selectRestrictionsForCpmPriceProduct,
    selectEditGroupValueInfo,
    (
        restrictions: ProductsListItemRestriction[],
        groupInfoValue: EditCpmPriceGroupDataValue
    ): ProductsListItemRestriction => {
        const { productRestrictionId } = groupInfoValue;
        const currentRestriction = restrictions.find(restriction => restriction.id === productRestrictionId);

        if (currentRestriction === undefined) {
            throw Error('задан неверный id продукта');
        }

        return currentRestriction;
    }
);

export const selectGroupTypesFromRestrictionsForCreation = createSelector(
    selectRestrictionsForCpmProduct,
    selectRestrictionForGroup,
    selectEditGroupValueInfo,
    selectClientFeatures,
    (
        restrictions: ProductsListItemRestriction[],
        selectedRestriction: ProductsListItemRestriction,
        groupInfoValue: EditCpmGroupDataValue,
        features: ClientFeatures
    ): [TypeModeGroupItems[], TitleItem[]] => {
        const sortIdxses = [
            GroupType.CPM_BANNER,
            GroupType.CPM_VIDEO,
            GroupType.CPM_AUDIO,
            GroupType.CPM_OUTDOOR,
            GroupType.CPM_INDOOR,
            GroupType.CPM_GEOPRODUCT
        ];

        const typeModeAvailableForCreation = restrictions
            .reduce((groupTypes: TypeModeGroupItems[], restriction: ProductsListItemRestriction) => {
                if (restriction.groupType === GroupType.CPM_BANNER) {
                    // Убираем второй тип группы CPM_BANNER
                    // Кусок логики связанный с особенностями бэкенда
                    if (
                        (selectedRestriction.groupType === GroupType.CPM_BANNER &&
                            selectedRestriction.id === restriction.id) ||
                        (selectedRestriction.groupType !== GroupType.CPM_BANNER &&
                            restriction.showConditionType === CpmGroupShowConditionType.USER_PROFILE)
                    ) {
                        groupTypes.push({
                            val: restriction.id,
                            groupType: restriction.groupType,
                            active: restriction.active,
                            text: GroupsHelpers.getTitleForGroupTypeChooser(
                                restriction.groupType,
                                groupInfoValue.campaignType,
                                features
                            ),
                            icon: GroupsHelpers.getIconNameForGroupType(
                                restriction.groupType,
                                groupInfoValue.campaignType,
                                features
                            ),
                            createdText: GroupsHelpers.getTitleForCreatedGroup(restriction.groupType, features)
                        });
                    }
                } else {
                    groupTypes.push({
                        val: restriction.id,
                        groupType: restriction.groupType,
                        active: restriction.active,
                        text: GroupsHelpers.getTitleForGroupTypeChooser(
                            restriction.groupType,
                            groupInfoValue.campaignType,
                            features
                        ),
                        icon: GroupsHelpers.getIconNameForGroupType(
                            restriction.groupType,
                            groupInfoValue.campaignType,
                            features
                        ),
                        createdText: GroupsHelpers.getTitleForCreatedGroup(restriction.groupType, features),
                        beta: true
                    });
                }

                return groupTypes;
            }, [])
            .sort(({ groupType: a }, { groupType: b }) => sortIdxses.indexOf(a) - sortIdxses.indexOf(b));

        return [
            typeModeAvailableForCreation,
            typeModeAvailableForCreation.map(item => ({
                text: item.text,
                val: item.val,
                active: item.active
            }))
        ];
    }
);

export const selectRegionsEditor = (state: State, groupId: GroupId): RegionsEditorState => {
    return state.regionsEditor[groupId];
};

export const selectPredictorData = createReReSelector(
    selectRegionTree,
    selectEditGroupValueInfo,
    selectRegionsEditor,
    getCurrentAudienceTargetingsText,
    (
        regions: Region[],
        groupValue: EditCpmGroupDataValue,
        selectedRegions: RegionsEditorState,
        audience: TextPredictor[]
    ): TextPredictor[] => {
        const regionData = PredictorHelpers.getListData(
            regions,
            !selectedRegions ? groupValue.regions.selectedMap : selectedRegions.selectedMap
        );

        return regionData.concat(audience);
    }
)((state, groupId) => groupId);

export const selectAudienceTargetingsData = createReReSelector(
    getAudienceTargetingsText,
    (audience: Record<string, SegmentCategoryText[]>): Record<string, SegmentCategoryText[]> => {
        return audience;
    }
)((state, groupId) => groupId);

export const selectEnabledAudienceTargetingsTypes = createReReSelector(
    (state: State, groupId: string) => state.editGroups.groups[groupId],
    (group: EditGroupData): AudienceTargetingsType[] => {
        switch (group.value.campaignType) {
            case CampaignType.TEXT: {
                return [AudienceTargetingsType.interests];
            }
            case CampaignType.CPM_PRICE: {
                return [AudienceTargetingsType.socialDemo, AudienceTargetingsType.interests];
            }
            case CampaignType.CPM_BANNER:
                return audienceTargetingsTypeOrderedList;
            default:
                return [];
        }
    }
)((state, groupId) => groupId);

export const selectPredictorGeo = createReReSelector(
    selectRegionTree,
    selectEditGroupValueInfo,
    selectRegionsEditor,
    (regions: Region[], groupValue: EditCpmGroupDataValue, selectedRegions: RegionsEditorState): PredictorGeo => {
        return PredictorHelpers.getRegion(
            regions,
            !selectedRegions ? groupValue.regions.selectedMap : selectedRegions.selectedMap
        );
    }
)((state, groupId) => groupId);

export const selectPredictor = createReReSelector(
    (state: State, groupId: GroupId) => state.predictor.data[groupId],
    (predictor?: PredictorData): PredictorData => {
        if (!predictor) {
            predictor = {
                isLoading: false,
                isLoadingRecommendation: false,
                isExpanderOpened: false
            };
        }

        return predictor;
    }
)((state, groupId) => groupId);

export const selectTypeGroup = (state: State, groupId: GroupId): GroupType =>
    state.editGroups.groups[groupId].value.groupType;

export const selectRateDevicesForPredictor = createReReSelector(
    (state: State, groupId: string) => state.editGroups.groups[groupId],
    (group: EditGroupData): PlatformType => {
        const result: PlatformType = {
            value: {},
            empty: false
        };

        if (!group || group.value.campaignType !== CampaignType.CPM_BANNER) {
            return {
                value: {},
                empty: true
            };
        }

        const { rateCorrections } = group.value;

        const devices = (rateCorrections[RateCorrectionType.device] &&
            rateCorrections[RateCorrectionType.device].items) as DeviceRateCorrectionCondition[];

        if (devices) {
            devices.forEach((device: DeviceRateCorrectionCondition) => {
                if (device.value === undefined) {
                    return;
                }

                const value = device.value;
                if (device.deviceType === DeviceRateCorrectionType.desktops) {
                    result.value.desktop = value;
                } else {
                    result.value.mobile = value;
                }

                if (device.deviceType === DeviceRateCorrectionType.ios) {
                    result.value.mobileOsType = GdMobileOsTypeInput.IOS;
                } else if (device.deviceType === DeviceRateCorrectionType.android) {
                    result.value.mobileOsType = GdMobileOsTypeInput.ANDROID;
                }
            });
        }

        return result;
    }
)((state, groupId) => groupId);

export const selectRecommendationData = createReReSelector(selectPredictor, (predictor?: PredictorData):
    | TextPredictor[]
    | undefined => {
    if (!predictor) {
        return;
    }

    const { bannerFormats } = predictor;

    return PredictorHelpers.getListRecommendationData(bannerFormats);
})((state, groupId) => groupId);

export const selectEnabledRateCorrectionsForGroups = (state: State, groupId: GroupId): Array<GdBidModifierType> => {
    const groupType = state.editGroups.groups[groupId].value.groupType;

    return (groupType && state.editGroups.allowedRateCorrectionsData[groupType]) || [];
};

export const getRecommendationsDataForEditGroups = createReReSelector(
    (state: State, groupId: string) => selectEditedGroupsRecommendationSet(state, groupId),
    (recommendationSet: EditPageRecommendationsData): RecommendationsAgregator => {
        return getRecommendationAgregatorData(recommendationSet, groups);
    }
)((state, groupId) => groupId);

export const getGroupModalRecommendations = createSelector(
    (state: State, id: string) => selectEditedGroupsRecommendationSet(state, id),
    (state: State) => state.editGroups.groups,
    (state: State, id: string) => id,
    (
        recommendationSet: EditPageRecommendationsData,
        editGroups: Record<GroupId, EditGroupData>,
        id: string
    ): RecommendationsModalState => {
        if (!recommendationSet) {
            return {
                isVisible: false,
                data: [],
                headerHint: ''
            };
        }

        const { recommendations } = recommendationSet;
        const title = editGroups[id].value.name;
        const titleHint = getRecommendationIdContext(groups, id);

        const data: Array<RecommendationModalType> = recommendations
            .map((recommendation: Recommendation) => {
                return {
                    recommendation: recommendation
                };
            })
            .sort(
                (a, b) =>
                    RecommendationСharacterWeight[a.recommendation.character] -
                    RecommendationСharacterWeight[b.recommendation.character]
            );

        return {
            isVisible: true,
            data: data,
            headerHint: `${title}, ${titleHint}`
        };
    }
);

export const selectEditedGroupsRecommendationSet = (state: State, groupId: GroupId): EditPageRecommendationsData => {
    return state.editGroups.recommendations[groupId];
};

export const selectPredictorOutdoorData = createReReSelector(
    filterGroupOutdoorMapData,
    selectGroupOutdoorValue,
    (
        outdoorMapData?: OutdoorMapCommonData,
        groupOutdoorValue?: {
            checkedByRegion: SelectedScreens;
        }
    ): TextPredictor[] => {
        if (!outdoorMapData || !groupOutdoorValue) {
            return PredictorHelpers.getDefaultOutdoorData();
        }
        const data = mapCheckedPageBlocksData(outdoorMapData, groupOutdoorValue.checkedByRegion, MapType.outdoor);

        if (data && data.length) {
            return PredictorHelpers.getOutdoorData(data, outdoorMapData.facilities, outdoorMapData.operators);
        }
        return PredictorHelpers.getDefaultOutdoorData();
    }
)((state, groupId) => groupId);

export const selectPredictorIndoorData = createReReSelector(
    filterGroupIndoorMapData,
    selectGroupIndoorValue,
    (
        indoorMapData?: IndoorMapCommonData,
        groupIndoorValue?: {
            checkedByRegion: SelectedScreens;
        }
    ): TextPredictor[] => {
        if (!indoorMapData || !groupIndoorValue) {
            return PredictorHelpers.getDefaultIndoorData();
        }

        const data = mapCheckedPageBlocksData(indoorMapData, groupIndoorValue.checkedByRegion, MapType.indoor);

        if (data && data.length) {
            return PredictorHelpers.getIndoorData(data, indoorMapData.facilities, indoorMapData.zones);
        }
        return PredictorHelpers.getDefaultOutdoorData();
    }
)((state, groupId) => groupId);

export const selectPredictorAllPageBlocks = (
    state: State,
    mapType: MapType
): { [id: string]: DisplayListItem[] } | undefined => {
    if (state.commonData[mapData[mapType]]) {
        return PlacementsMapHelpers.filterDeletedPlacements(state.commonData[mapData[mapType]].screenGroupsByRegion);
    }

    return;
};

export const selectMapEditor = (state: State, mapType: MapType): OutdoorMapEditorState | IndoorMapEditorState => {
    return state[mapEditor[mapType]];
};

export const selectPredictorEditorOutdoorData = createSelector(
    (state: State) => state.outdoorMapEditor,
    filterGroupOutdoorMapData,
    (mapEditData: OutdoorMapEditorState, mapCommonData: OutdoorMapCommonData): TextPredictor[] => {
        let listData: TextPredictor[] = [];

        if (mapEditData.checkedByRegion && mapCommonData) {
            const data = mapCheckedPageBlocksData(mapCommonData, mapEditData.checkedByRegion, MapType.outdoor);

            if (data && data.length) {
                listData = PredictorHelpers.getOutdoorData(
                    data,
                    mapCommonData.facilities,
                    get(mapCommonData, 'operators')
                );
            }
        } else {
            listData = PredictorHelpers.getDefaultOutdoorData();
        }

        return listData;
    }
);

export const selectPredictorEditorIndoorData = createSelector(
    (state: State) => state.indoorMapEditor,
    filterGroupIndoorMapData,
    (mapEditData: IndoorMapEditorState, mapCommonData: IndoorMapCommonData): TextPredictor[] => {
        let listData: TextPredictor[] = [];

        if (mapEditData.checkedByRegion && mapCommonData) {
            const data = mapCheckedPageBlocksData(mapCommonData, mapEditData.checkedByRegion, MapType.indoor);

            if (data && data.length) {
                listData = PredictorHelpers.getIndoorData(data, mapCommonData.facilities, get(mapCommonData, 'zones'));
            }
        } else {
            listData = PredictorHelpers.getDefaultIndoorData();
        }

        return listData;
    }
);

export const selectPageBlocksFromEditorMap = (
    state: State,
    allPageBlocks: {
        [id: string]: DisplayListItem[];
    },
    mapType: MapType
): {
    pageBlocks?: DisplayPageBlock[];
    groupId: GroupId;
} => {
    const map = state[mapEditor[mapType]];
    let pageBlocks: DisplayPageBlock[] = [];

    if (mapEditor && map.checkedByRegion) {
        switch (mapType) {
            case MapType.outdoor:
                pageBlocks = PlacementsMapHelpers.outdoorCheckedByRegionToPageBlocks(
                    allPageBlocks,
                    map.checkedByRegion
                );
                break;
            case MapType.indoor:
                pageBlocks = PlacementsMapHelpers.indoorCheckedByRegionToPageBlocks(allPageBlocks, map.checkedByRegion);
                break;
            default:
                pageBlocks = [];
        }
    }

    return {
        pageBlocks: pageBlocks.length ? pageBlocks : undefined,
        groupId: map.groupId
    };
};

export const selectPredictorEditor = createSelector(
    (state: State, groupId: GroupId) => state.predictor.data[groupId],
    (state: State) => state.predictorEditor,
    (predictor: PredictorData, predictorEditor?: PredictorData): PredictorData => {
        if (!predictorEditor) {
            predictorEditor = predictor;
        }

        return predictorEditor;
    }
);

export const selectRateCorrectionsDemography = createReReSelector(
    selectEditGroupValueInfo,
    (groupValue: EditCpmGroupDataValue): GdBidModifierDemographicInput[] | undefined => {
        if (!groupValue.rateCorrections || !groupValue.rateCorrections[RateCorrectionType.demography]) {
            return;
        }

        const items = groupValue.rateCorrections[RateCorrectionType.demography]
            .items as DemographyRateCorrectionCondition[];

        return items.map((item: DemographyRateCorrectionCondition) => {
            let value;

            if (item.value !== undefined) {
                value = getMultiplierFromRateCorrectionValue(item.value);
            }
            return {
                age: item.age,
                multiplier: value,
                gender: item.gender
            };
        });
    }
)((state, groupId) => groupId);

export const selectAudienceRetargetingEditor = (
    state: State,
    groupId: GroupId
): RetargetingConditionData | undefined => {
    const retargetingAudienceEditor = state.retargetingAudienceEditor[groupId];

    if (retargetingAudienceEditor && !retargetingAudienceEditor.isDeleting) {
        return retargetingAudienceEditor;
    }

    return;
};
